import React, {useCallback, useEffect, useRef, useState} from 'react';
import {MdKeyboardArrowRight} from 'react-icons/md';

export interface NavSelectorItem {
    id: string;
    name: string;
    count?: number;
}

interface SplitNavSelectorProps {
    /** Label above the selector (e.g., "Folders", "Groups") */
    label: string;
    /** Currently selected item name */
    currentName: string;
    /** Items to show in the dropdown */
    items: NavSelectorItem[];
    /** ID of the currently active item */
    activeId?: string;
    /** Called when an item is selected from the dropdown */
    onSelect: (item: NavSelectorItem) => void;
    /** Called when clicking the name (e.g., navigate back) */
    onBack: () => void;
    /** Called when clicking the label (optional, defaults to onBack) */
    onLabelClick?: () => void;
    /** Show the arrow separator before the name */
    showArrow?: boolean;
    /** Dropdown header text */
    dropdownLabel?: string;
}

const SplitNavSelector: React.FC<SplitNavSelectorProps> = ({
    label,
    currentName,
    items,
    activeId,
    onSelect,
    onBack,
    onLabelClick,
    showArrow = false,
    dropdownLabel = `Switch ${label.toLowerCase()}`,
}) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handleClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);

    const handleSelect = useCallback((item: NavSelectorItem) => {
        setOpen(false);
        if (item.id !== activeId) {
            onSelect(item);
        }
    }, [activeId, onSelect]);

    return (
        <div className="flex flex-col items-start mr-4" ref={ref}>
            <span
                className="text-xs text-gray-500 mb-1 cursor-pointer hover:text-blue-500"
                onClick={onLabelClick ?? onBack}
            >
                {label}
            </span>
            <div className="relative">
                <div className="flex items-center -ml-2.5">
                    {showArrow && <MdKeyboardArrowRight className="text-lg text-gray-400"/>}
                    <button
                        type="button"
                        className="px-2 py-1 rounded-l-lg cursor-pointer transition-colors hover:bg-gray-50 group"
                        onClick={onBack}
                        title={`Back to ${label.toLowerCase()}`}
                    >
                        <span className="text-xl font-semibold text-gray-800 group-hover:text-blue-600">
                            {currentName}
                        </span>
                    </button>
                    {items.length > 1 && (
                        <button
                            type="button"
                            className={`px-1.5 py-1.5 rounded-r-lg cursor-pointer transition-colors border-l border-gray-200 ${
                                open ? 'bg-indigo-50' : 'hover:bg-gray-100'
                            }`}
                            onClick={() => setOpen(v => !v)}
                            title={dropdownLabel}
                        >
                            <svg
                                className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
                                fill="none" viewBox="0 0 24 24" stroke="currentColor"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                            </svg>
                        </button>
                    )}
                </div>
                {open && items.length > 1 && (
                    <div className="absolute top-full left-0 mt-1 w-72 bg-white rounded-xl border border-gray-200 shadow-xl z-50 py-1.5 max-h-80 overflow-auto">
                        <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                            {dropdownLabel}
                        </div>
                        {items.map((item) => {
                            const isActive = item.id === activeId;
                            return (
                                <button
                                    key={item.id}
                                    type="button"
                                    className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2.5 ${
                                        isActive
                                            ? 'bg-indigo-50 text-indigo-700'
                                            : 'text-gray-700 hover:bg-gray-50'
                                    }`}
                                    onClick={() => handleSelect(item)}
                                >
                                    <span className={`truncate flex-1 ${isActive ? 'font-medium' : ''}`}>
                                        {item.name}
                                    </span>
                                    {item.count != null && (
                                        <span className={`text-[11px] shrink-0 ${isActive ? 'text-indigo-400' : 'text-gray-400'}`}>
                                            {item.count}
                                        </span>
                                    )}
                                    {isActive && (
                                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0"/>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SplitNavSelector;
